import { forwardRef, useState } from 'react'

import { FieldError } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import clsx from 'clsx'

import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'

import { AppIcon } from '../app-icon'
import { AllIcons } from '../app-icon/types'

import { BlockSelect, OptionItem } from './components/select-block'
import { SelectedItem } from './components/selected-item'
import s from './styles.module.scss'

export interface Option {
  value: string
  name: string
}

interface Props {
  error?: FieldError | boolean
  placeholder?: string
  value?: string[]
  title?: string
  options?: OptionItem[]
  onChange?: (data?: Option[]) => void
}

export const SelectMulti = forwardRef<HTMLDivElement, Props>(
  ({ placeholder, options, value, title, error, onChange }, ref) => {
    const { t } = useTranslation()
    const { closeDialog, openDialog } = useDialogBottomSheet()

    const [optionList, setOptionList] = useState<OptionItem[] | undefined>(() =>
      options?.map((option) => ({
        ...option,
        isSelected: !!value?.includes(option?.value),
      })),
    )

    const onSelectOption = (option?: OptionItem[]) => {
      const selectedOptions = option?.filter((option) => option?.isSelected)

      setOptionList(option)

      onChange?.(selectedOptions)

      closeDialog()
    }

    const onSelectAll = () => {
      const result = options?.map((option) => ({
        ...option,
        isSelected: true,
      }))

      setOptionList(result)
      onChange?.(result)
      closeDialog()
    }

    const onRemoveOption = (value: OptionItem) => {
      const newListSelected = optionList?.map((option) => {
        if (option.value === value.value) {
          option.isSelected = false
        }

        return option
      })

      setOptionList(newListSelected)
    }

    const onShowDialogBottomSheetStore = () => {
      openDialog({
        child: (
          <div className={s.dialog}>
            <div className={s.header}>
              <p className={s.title}>{title ? title : null}</p>

              <p
                className={s.selectAll}
                onClick={onSelectAll}
              >
                {t('select_all')}
              </p>
            </div>

            <BlockSelect
              options={optionList}
              onChange={onSelectOption}
            />
          </div>
        ),
      })
    }

    const listSelected = optionList?.filter((option) => option?.isSelected)

    return (
      <div className={s.wrapper}>
        <div
          className={clsx(s.container, error && s.error)}
          ref={ref}
        >
          <div
            className={s.content}
            onClick={onShowDialogBottomSheetStore}
          >
            {listSelected?.length ? (
              <span className={s.value}>{`${t('selected')} ${
                listSelected?.length
              }`}</span>
            ) : (
              <span className={s.placeholder}>{placeholder}</span>
            )}
          </div>

          <AppIcon
            id={AllIcons.ARROW_BOTTOM}
            className={s.arrow}
            height={24}
            width={24}
          />
        </div>

        {listSelected?.length ? (
          <div className={s.wrapperSelected}>
            {listSelected?.map((selected) => (
              <SelectedItem
                title={selected?.name}
                onClick={() => onRemoveOption(selected)}
              />
            ))}
          </div>
        ) : null}
      </div>
    )
  },
)

SelectMulti.displayName = 'SelectMulti'
